import React from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { defaultImage } from '@assets'
import { Icon, Text } from '../atoms'

interface NotePersonDetailsProps {
  name: string
  avatarUrl?: string | null
  hashtags?: string[]
  // « il y a 6 jours », or null when nothing was exchanged yet.
  lastExchange: string | null
  otherNotesCount: number
  // « 28 sept. 2026 », or null while the note is not saved yet.
  writtenOn: string | null
  onOpenProfile: () => void
  onOpenNotes: () => void
  // Left out when the person of the note can't be changed.
  onChange?: () => void
}

// What a tap on the person's line opens on the phone: who the note is about,
// when you last talked, your other notes on them, and how to change the person.
export const NotePersonDetails = ({
  name,
  avatarUrl,
  hashtags,
  lastExchange,
  otherNotesCount,
  writtenOn,
  onOpenProfile,
  onOpenNotes,
  onChange,
}: NotePersonDetailsProps) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const firstName = name.split(' ')[0]

  return (
    <View testID="note-person-details" style={styles.container}>
      <View style={styles.header}>
        <Image source={avatarUrl ? { uri: avatarUrl } : defaultImage} style={styles.avatar} />
        <View style={styles.identity}>
          <Text type="subtitleBold" numberOfLines={1}>
            {name}
          </Text>
          {!!hashtags?.length && (
            <Text type="bodySmall" color={theme.colors.primary} numberOfLines={1}>
              {hashtags.join(' ')}
            </Text>
          )}
        </View>
      </View>
      <View style={styles.divider} />
      <View style={styles.row}>
        <Icon name="messageCircle" size={18} color={theme.colors.greyBlue} />
        <Text type="body" color={theme.colors.text} style={styles.rowText}>
          {lastExchange ? (
            <>
              {t('notesForm.lastExchange')}{' '}
              <Text type="bodyBold" color={theme.colors.text}>
                {lastExchange}
              </Text>
            </>
          ) : (
            t('notesForm.noExchangeYet')
          )}
        </Text>
      </View>
      <View style={styles.row}>
        <Icon name="note" size={18} color={theme.colors.greyBlue} />
        <Text type="body" color={theme.colors.text} style={styles.rowText}>
          {t('notesForm.otherNotes', { count: otherNotesCount, name: firstName })}
        </Text>
        {otherNotesCount > 0 && (
          <TouchableOpacity testID="note-person-notes" onPress={onOpenNotes}>
            <Text type="body" color={theme.colors.primary}>
              {t('notesForm.see')}
            </Text>
          </TouchableOpacity>
        )}
      </View>
      {!!writtenOn && (
        <View style={styles.row}>
          <Icon name="calendar" size={18} color={theme.colors.greyBlue} />
          <Text type="body" color={theme.colors.text} style={styles.rowText}>
            {t('notesForm.noteWrittenOn', { date: writtenOn })}
          </Text>
        </View>
      )}
      <View style={styles.divider} />
      <View style={styles.actions}>
        <TouchableOpacity
          testID="note-person-profile"
          style={styles.profileButton}
          onPress={onOpenProfile}
        >
          <Icon name="user" size={17} color={theme.colors.primary} />
          <Text type="bodyBold" color={theme.colors.primary}>
            {t('notesForm.seeProfile')}
          </Text>
        </TouchableOpacity>
        {onChange && (
          <TouchableOpacity testID="note-person-change" onPress={onChange}>
            <Text type="body" color={theme.colors.primary}>
              {t('notesForm.changePerson')}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      width: '100%',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 14,
    },
    avatar: {
      width: 56,
      height: 56,
      borderRadius: 28,
    },
    identity: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },
    divider: {
      height: 1,
      backgroundColor: theme.colors.grey20,
      marginVertical: theme.spacing.lg,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginBottom: theme.spacing.md,
    },
    rowText: {
      flex: 1,
    },
    actions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 18,
    },
    // The one outlined button of the sheet: white, 2 px blue ring.
    profileButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      height: 40,
      paddingHorizontal: 18,
      borderRadius: theme.border.jumbo,
      borderWidth: 2,
      borderColor: theme.colors.primary,
    },
  })
